/** Partner console — Commercials list (reseller only).
 *
 * Follows the same list + filter + top-right action-button convention as
 * PartnerOrganizationsPage. The "Add commercial" form lives at its own
 * route (/partner/commercials/new → PartnerAddCommercialPage). */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";

import { PartnerPageHeader } from "@/components/PartnerLayout";
import { Spinner } from "@/components/Spinner";
import {
  useDeleteCommercial,
  useDisassociateCommercial,
  useInviteCommercial,
  usePartnerCommercials,
  usePartnerMe,
} from "@/lib/partner";
import type { CommercialBreakdownRow } from "@/lib/types";


export default function PartnerCommercialsPage() {
  const { t } = useTranslation();
  const me = usePartnerMe();
  const commercials = usePartnerCommercials(true);
  const navigate = useNavigate();
  const [filter, setFilter] = useState("");

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const rows = commercials.data?.commercials ?? [];
    if (!q) return rows;
    return rows.filter((c) =>
      `${c.name} ${c.email ?? ""} ${c.referral_code ?? ""} ${c.legal_name ?? ""}`
        .toLowerCase()
        .includes(q),
    );
  }, [commercials.data, filter]);

  if (me.isLoading) return <Spinner fullscreen label={t("common.loading")} />;
  if (me.data && me.data.kind !== "reseller")
    return <Navigate to="/partner" replace />;

  const allRows = commercials.data?.commercials ?? [];

  return (
    <>
      <PartnerPageHeader
        eyebrow={t("nav.partner.commercials")}
        title={t("partner.commercials.title")}
        description={t("partner.commercials.lead")}
        action={
          <button
            type="button"
            className="btn-primary text-sm"
            onClick={() => navigate("/partner/commercials/new")}
          >
            + {t("partner.commercials.add")}
          </button>
        }
      />

      <section className="card space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
            {t("partner.commercials.listTitle", "Commercials")}
          </h2>
          {allRows.length > 0 && (
            <input
              className="input h-8 w-64 max-w-full text-sm"
              placeholder={t(
                "partner.commercials.filterPlaceholder",
                "Filter by name, email…",
              )}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          )}
        </div>

        {commercials.isLoading ? (
          <p className="text-sm text-nb-text-3">{t("common.loading")}</p>
        ) : allRows.length === 0 ? (
          <p className="text-sm text-nb-text-3">
            {t("partner.commercials.noneYet")}
          </p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-nb-text-3">
            {t("partner.commercials.noMatch", "No commercials match your filter.")}
          </p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase text-nb-text-3">
              <tr>
                <th className="py-1">{t("partner.org.colName")}</th>
                <th className="py-1">{t("partner.commercials.colCode")}</th>
                <th className="py-1">{t("partner.commercials.colOrgs")}</th>
                <th className="py-1">{t("partner.commercials.colGenerated")}</th>
                <th className="py-1">{t("partner.commercials.colUnpaid")}</th>
                <th className="py-1">{t("partner.commercials.colAccount")}</th>
                <th className="py-1">{t("partner.commercials.colTerms")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="border-t border-nb-border">
                  <td className="py-1.5 text-nb-text">
                    {c.name}
                    {c.is_company && c.legal_name && (
                      <span className="ml-2 rounded bg-nb-mint/10 px-1.5 py-0.5 text-[10px] text-nb-text-3">
                        {c.legal_name}
                      </span>
                    )}
                    {c.status === "suspended" && (
                      <span className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-300">
                        {t("platform.common.suspended", "suspended")}
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 font-mono text-nb-text-3">
                    {c.referral_code}
                  </td>
                  <td className="py-1.5 text-nb-text-2">{c.org_count}</td>
                  <td className="py-1.5 text-nb-text">
                    €{c.commission_total_eur}
                  </td>
                  <td className="py-1.5 text-nb-text-2">
                    €{c.commission_unpaid_eur}
                  </td>
                  <td className="py-1.5">
                    <CommercialAccountCell commercial={c} />
                  </td>
                  <td className="py-1.5">
                    <span className="text-nb-text-2">
                      {c.commission_model === "fixed"
                        ? `€${c.fixed_session_eur} / session`
                        : `${c.rate_session_pct}% / session`}
                    </span>
                    <button
                      type="button"
                      className="btn-ghost ml-2 text-xs"
                      onClick={() =>
                        navigate(`/partner/commercials/${c.id}/edit`)
                      }
                    >
                      {t("partner.commercials.edit", "Edit")}
                    </button>
                    <CommercialRowActions commercial={c} />
                  </td>
                </tr>
              ))}
              {(commercials.data?.unattributed.org_count ?? 0) > 0 && (
                <tr className="border-t border-nb-border text-nb-text-3">
                  <td className="py-1.5 italic" colSpan={2}>
                    {t("partner.commercials.house")}
                  </td>
                  <td className="py-1.5">
                    {commercials.data?.unattributed.org_count}
                  </td>
                  <td className="py-1.5">
                    €{commercials.data?.unattributed.commission_total_eur}
                  </td>
                  <td className="py-1.5">
                    €{commercials.data?.unattributed.commission_unpaid_eur}
                  </td>
                  <td />
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

/** Account-opening email control for one commercial row.
 *
 * Label logic: has_login → "Active" badge + subtle "Resend";
 * invite_pending → "Invited" badge + "Resend"; neither → prominent
 * "Send account email". Shows a transient inline success / error. */
function CommercialAccountCell({
  commercial,
}: {
  commercial: CommercialBreakdownRow;
}) {
  const { t } = useTranslation();
  const invite = useInviteCommercial();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [err, setErr] = useState(false);

  const send = () => {
    setErr(false);
    setSentTo(null);
    invite.mutate(commercial.id, {
      onSuccess: (res) => setSentTo(res.email || commercial.email),
      onError: () => setErr(true),
    });
  };

  const hasLogin = commercial.has_login;
  const pending = commercial.invite_pending;

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-2">
        {hasLogin ? (
          <>
            <span className="rounded bg-nb-mint/15 px-1.5 py-0.5 text-[10px] text-nb-mint">
              {t("partner.commercials.active")}
            </span>
            <button
              type="button"
              className="text-xs text-nb-text-3 hover:text-nb-mint disabled:opacity-50"
              disabled={invite.isPending}
              onClick={send}
            >
              {t("partner.commercials.resend")}
            </button>
          </>
        ) : pending ? (
          <>
            <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-300">
              {t("partner.commercials.invited")}
            </span>
            <button
              type="button"
              className="text-xs text-nb-mint hover:underline disabled:opacity-50"
              disabled={invite.isPending}
              onClick={send}
            >
              {t("partner.commercials.resend")}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="btn-ghost text-xs"
            disabled={invite.isPending}
            onClick={send}
          >
            {t("partner.commercials.sendAccountEmail")}
          </button>
        )}
      </div>
      {sentTo && (
        <span className="text-[10px] text-nb-mint" role="status">
          {t("partner.commercials.sentTo", { email: sentTo })}
        </span>
      )}
      {err && (
        <span className="text-[10px] text-cvp-danger" role="alert">
          {t("partner.commercials.resendError")}
        </span>
      )}
    </div>
  );
}

/** Per-row destructive/lifecycle actions for one commercial.
 *
 * - Delete: two-step inline confirm; on 409 payment-pending the backend's
 *   `detail` (with the outstanding €) is shown inline and the row stays.
 * - Disassociate: a small popover with an effective-date input; on confirm the
 *   commercial's orgs move to NexBasira-direct. 409 shows `detail` inline. */
function CommercialRowActions({
  commercial,
}: {
  commercial: CommercialBreakdownRow;
}) {
  const { t } = useTranslation();
  const del = useDeleteCommercial();
  const disassociate = useDisassociateCommercial();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showDisassoc, setShowDisassoc] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState(
    () => new Date().toISOString().slice(0, 10),
  );
  const [error, setError] = useState<string | null>(null);
  const [moved, setMoved] = useState(false);

  const readDetail = (e: unknown) =>
    (e as { response?: { data?: { detail?: string } } })?.response?.data
      ?.detail ?? null;

  const doDelete = () => {
    setError(null);
    del.mutate(commercial.id, {
      // On success the row disappears via query invalidation.
      onError: (e) => {
        setConfirmDelete(false);
        setError(readDetail(e) || t("partner.commercials.deleteError"));
      },
    });
  };

  const doDisassociate = () => {
    setError(null);
    disassociate.mutate(
      { id: commercial.id, effective_date: effectiveDate },
      {
        onSuccess: () => {
          setShowDisassoc(false);
          setMoved(true);
        },
        onError: (e) =>
          setError(readDetail(e) || t("partner.commercials.disassociateError")),
      },
    );
  };

  const busy = del.isPending || disassociate.isPending;

  return (
    <span className="relative ml-2 inline-flex flex-col gap-0.5 align-top">
      <span className="inline-flex items-center gap-2">
        <button
          type="button"
          className="text-xs text-nb-text-3 hover:text-nb-mint disabled:opacity-50"
          disabled={busy}
          onClick={() => {
            setError(null);
            setConfirmDelete(false);
            setShowDisassoc((v) => !v);
          }}
        >
          {t("partner.commercials.disassociate")}
        </button>
        {confirmDelete ? (
          <>
            <button
              type="button"
              className="text-xs font-medium text-cvp-danger hover:underline disabled:opacity-50"
              disabled={busy}
              onClick={doDelete}
            >
              {t("partner.commercials.confirmDelete")}
            </button>
            <button
              type="button"
              className="text-xs text-nb-text-3 hover:text-nb-text disabled:opacity-50"
              disabled={busy}
              onClick={() => setConfirmDelete(false)}
            >
              {t("common.cancel")}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="text-xs text-nb-text-3 hover:text-cvp-danger disabled:opacity-50"
            disabled={busy}
            onClick={() => {
              setError(null);
              setShowDisassoc(false);
              setConfirmDelete(true);
            }}
          >
            {t("partner.commercials.delete")}
          </button>
        )}
      </span>

      {showDisassoc && (
        <div className="absolute right-0 top-full z-20 mt-1 w-64 space-y-2 rounded-md border border-nb-border bg-nb-surface p-3 text-left shadow-lg">
          <p className="text-xs text-nb-text-2">
            {t("partner.commercials.moveToDirect")}
          </p>
          <label className="block text-[11px] text-nb-text-3">
            {t("partner.commercials.effectiveDate")}
            <input
              type="date"
              className="input mt-1 h-8 w-full text-sm"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
            />
          </label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn-primary text-xs"
              disabled={busy}
              onClick={doDisassociate}
            >
              {disassociate.isPending
                ? t("common.saving")
                : t("partner.commercials.disassociate")}
            </button>
            <button
              type="button"
              className="btn-ghost text-xs"
              disabled={busy}
              onClick={() => setShowDisassoc(false)}
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}

      {moved && (
        <span className="text-[10px] text-nb-mint" role="status">
          {t("partner.commercials.disassociated")}
        </span>
      )}
      {error && (
        <span className="max-w-[16rem] text-[10px] text-cvp-danger" role="alert">
          {error}
        </span>
      )}
    </span>
  );
}
